import { UserProfile } from '@watchlist/shared';

const STORAGE_KEY = 'wl_profile_cache';
const PERSIST_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const MAX_ENTRIES = 200;

export interface PersistedProfileEntry {
  uid: string;
  displayName: string;
  username?: string;
  photoURL?: string;
  photoThumbnailURL?: string;
  persistedAt: number;
}

export function loadPersistedProfiles(): Record<string, PersistedProfileEntry> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, PersistedProfileEntry>;
    const now = Date.now();
    const result: Record<string, PersistedProfileEntry> = {};
    for (const [userId, entry] of Object.entries(parsed)) {
      if (entry && entry.persistedAt && (now - entry.persistedAt) < PERSIST_TTL_MS) {
        result[userId] = entry;
      }
    }
    return result;
  } catch {
    return {};
  }
}

export function persistProfile(userId: string, profile: Partial<UserProfile>): void {
  if (!userId || !profile) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const stored: Record<string, PersistedProfileEntry> = raw ? JSON.parse(raw) : {};
    stored[userId] = {
      uid: userId,
      displayName: profile.displayName || '',
      username: profile.username,
      photoURL: profile.photoURL,
      photoThumbnailURL: profile.photoThumbnailURL,
      persistedAt: Date.now(),
    };
    // Prune to MAX_ENTRIES keeping most recently persisted
    const entries = Object.entries(stored);
    if (entries.length > MAX_ENTRIES) {
      entries.sort((a, b) => b[1].persistedAt - a[1].persistedAt);
      const pruned = Object.fromEntries(entries.slice(0, MAX_ENTRIES));
      localStorage.setItem(STORAGE_KEY, JSON.stringify(pruned));
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    }
  } catch {
    // localStorage unavailable or quota exceeded — degrade gracefully
  }
}

export function removePersistedProfile(userId: string): void {
  if (!userId) return;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored: Record<string, PersistedProfileEntry> = JSON.parse(raw);
    delete stored[userId];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // ignore
  }
}

export function clearPersistedProfiles(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
